import * as z from 'zod';

import {
    BILLING_TYPE,
    CALCULATION_TYPE,
    CAPPING,
    DAY_OF_WEEK,
    FREE_TIME_TYPE,
    PAYMENT_TYPE,
    PRICING_PLAN_STATUS,
    TICK_TYPE,
} from 'src/features/pricingPlan/utils/types';
import { VISITOR_TYPE } from 'src/features/visitor/types';
import { CURRENCY } from 'src/types';
import { validationMessagesMap } from 'src/utils/messages';

export type PricingPlanFormSchemaValues = z.infer<typeof pricingPlanFormSchema>;
export const pricingPlanFormSchema = z
    .object({
        translations: z
            .array(
                z.object({
                    language: z.string(),
                    name: z.string().min(1),
                    description: z.string().optional(),
                }),
            )
            .min(1),
        calculation_type: z.enum(CALCULATION_TYPE),
        billing_type: z.enum(BILLING_TYPE),
        status: z.enum(PRICING_PLAN_STATUS),
        valid_from: z.string(),
        valid_to: z.string().optional().nullable(),
        tax_percent: z
            .string()
            .trim()
            .min(1, {
                message: validationMessagesMap.validationErrorRequired,
            })
            .regex(/^-?\d{0,20}(?:\.\d{0,4})?$/),
        currency: z.enum(CURRENCY),
        // The "rules" section (free/exit time) does not apply to fixed-price plans, so free_time_type
        // is only required for the other calculation types (enforced in the superRefine below).
        free_time_type: z.enum(FREE_TIME_TYPE).nullable(),
        free_time_cap_minutes: z.number().nullable(),
        free_time_minutes: z.number().nullable(),
        exit_time: z.number().nullable(),
        exit_time_grace: z.number().nullable(),
        capping: z.enum(CAPPING).optional(),
        tick: z
            .object({
                tick_type: z.enum(TICK_TYPE),
                tick_minutes: z.number(),
            })
            .optional()
            .nullable(),
        fixed_rate: z
            .object({
                rate_with_tax: z.string().trim(),
            })
            .optional()
            .nullable(),
        identity: z.string().optional(),
        extra_free_time: z.boolean().optional().nullable(),
        extra_free_time_minutes: z.number().min(1).optional().nullable(),
        extra_free_time_activation_remaining_minutes: z.number().min(1).optional().nullable(),
    })
    .superRefine((data, ctx) => {
        if (data.calculation_type !== CALCULATION_TYPE.FIXED_PRICE && !data.free_time_type) {
            ctx.addIssue({
                path: ['free_time_type'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
        if (data.extra_free_time) {
            if (!data.extra_free_time_minutes) {
                ctx.addIssue({
                    path: ['extra_free_time_minutes'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
            if (!data.extra_free_time_activation_remaining_minutes) {
                ctx.addIssue({
                    path: ['extra_free_time_activation_remaining_minutes'],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
        }
        if (data.calculation_type === CALCULATION_TYPE.FIXED_PRICE && !data.fixed_rate?.rate_with_tax) {
            ctx.addIssue({
                path: ['fixed_rate.rate_with_tax'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
    });

export type PricingPlanCapFormSchemaValues = z.infer<typeof pricingPlanCapFormSchema>;
export const pricingPlanCapFormSchema = z.object({
    cap_type: z.enum(TICK_TYPE),
    cap_minutes: z.number(),
    rate_with_tax: z.number(),
});

const tickRateFormSchema = z.object({
    from_time: z.string(),
    to_time: z.string(),
    rate_with_tax: z.number().min(0),
    max_amount: z.number().min(0).nullable().optional(),
});

// One saving creates one band: it runs from its start day at "from_time" to its end day at "to_time", so it may
// span several days
export type PricingPlanTickRateCreateValues = z.infer<typeof pricingPlanTickRateCreate>;
export const pricingPlanTickRateCreate = tickRateFormSchema.extend({
    start_day: z.enum(DAY_OF_WEEK),
    end_day: z.enum(DAY_OF_WEEK),
});

export type PricingPlanTierFormSchemaValues = z.infer<typeof pricingPlanTierFormSchema>;
export const pricingPlanTierFormSchema = z.object({
    start_minute: z.number().min(0),
    end_minute: z.number().nullable().optional(),
    rate_with_tax: z.number(),
    tick_type: z.enum(TICK_TYPE),
    tick_minutes: z.number(),
});

export type PricingPlanPeriodAdjustmentFormSchemaValues = z.infer<typeof pricingPlanPeriodAdjustmentFormSchema>;
export const pricingPlanPeriodAdjustmentFormSchema = z.object({
    payment_type: z.enum(PAYMENT_TYPE),
    valid_from: z.string(),
    valid_to: z.string().nullable().optional(),
    day_of_week: z.enum(DAY_OF_WEEK).nullable().optional(),
    from_time: z.string().nullable().optional(),
    to_time: z.string().nullable().optional(),
    adjustment_percent: z.string().nullable().optional(),
    adjustment_amount: z.number().nullable().optional(),
});

// The form fills in exactly one band
export type PricingPlanTickRateFormSchemaValues = z.infer<typeof pricingPlanTickRateFormSchema>;
export const pricingPlanTickRateFormSchema = pricingPlanTickRateCreate;

// The period of the pricing simulation screen. The end picker is limited to the start, and the backend refuses a
// period ending before it starts - so the schema only requires both ends
export type PricingSimulationFormSchemaValues = z.infer<typeof pricingSimulationFormSchema>;
export const pricingSimulationFormSchema = z.object({
    start: z.string().min(1),
    end: z.string().min(1),
    // "plan", or "<kind>:<object id>" of one of the objects the plan is used in
    context: z.string().min(1),
    // Which assignments of that object apply - only an object resolves its tariff through assignments
    visitor_type: z.nativeEnum(VISITOR_TYPE),
});
